import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {
  Table,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink as NavLinkBase,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import { getDocumentDetails, getDocumentPath, getPathName } from 'redux/actions/documentActions';
import { selectCurrentClient } from 'redux/actions/clientActions';

import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { ReactComponent as EditIcon } from 'assets/icon-edit.svg';
import { ReactComponent as DownloadIcon } from 'assets/icon-download.svg';
import { ButtonIcon } from 'shared/components/Buttons';
import { colors, fonts, fontSizes, device } from 'shared/defaultTheme';
import ConfirmModal from 'shared/components/ConfirmModal';
import AlertModal from 'shared/components/AlertModal';

import { TdNameDocument, TdNameClient } from '../styles';

const HomeTab = ({
  activeTab,
  toggleDocument,
  clients,
  lastCreatedDocuments,
  lastUpdatedDocuments,
  refreshDocumentsData,
}) => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useSelector((state) => state.theme);
  const { mainColor } = theme;
  const { isCustomer, mail, authToken } = useSelector((state) => state.auth);
  const [SelectedRemoveDocument, setSelectedRemoveDocument] = useState(null);
  const [showConfirmRemoveDocument, setShowConfirmRemoveDocument] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [showSuccessRemove, setShowSuccessRemove] = useState();
  const [showErrorRemove, setShowErrorRemove] = useState(false);
  const [selecteIndex, setSelecteIndex] = useState();

  const [{ loading: loadingDelete }, deleteDocumentation] = customAreaApi(
    {
      method: 'DELETE',
    },
    { manual: true }
  );

  const [{ loading: loadingDownload }, getDownload] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const [, getOnePathItem] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const onToggleErrorRemove = () => setShowErrorRemove((currentShow) => !currentShow);

  const onToggleSuccessRemove = () => setShowSuccessRemove((currentShow) => !currentShow);

  const onEdit = async (currentDocument) => {
    localStorage.removeItem('documentFormState');
    localStorage.removeItem('localizacionPath');
    localStorage.removeItem('setPath');
    const { clientNavisionId, clientId, id } = currentDocument;
    try {
      const {
        data: { data },
      } = await getOnePathItem({
        url: `${window.API_V1}/cli/items/${clientNavisionId}/${clientId}/${encodeURIComponent(
          mail
        )}/${id}`,
      });
      const pathIds = data.pathId.split('/');
      const clearedPathIds = pathIds.filter((x) => x.length > 0);
      clearedPathIds.splice(-1);
      let currentPath = '';
      clearedPathIds.forEach((pathName) => {
        currentPath += `${pathName}/`;
      });
      const findClientName = clients.find((x) => x.clientId === currentDocument.clientId);
      dispatch(selectCurrentClient(findClientName));
      localStorage.setItem('documentFormState', JSON.stringify(currentDocument));
      localStorage.setItem('localizacionPath', data.pathName);
      localStorage.setItem('setPath', currentPath);
      dispatch(getDocumentPath(currentPath));
      dispatch(getPathName(data.pathName));
      dispatch(getDocumentDetails(currentDocument));
      history.push('/documentation', {
        params: { documentType: 'new_word', currentDocument },
      });
    } catch (error) {
      setMessageError(t('custom_field_operation_error_message'));
      onToggleErrorRemove();
    }
  };

  const NavHome = styled(Nav)`
    border-bottom: 1px solid ${({ color }) => color};
  `;
  const NavLink = styled(NavLinkBase)`
    border-color: ${({ border }) => border} !important;
    color: ${({ color }) => color};
    background-color: ${({ background }) => background} !important;
    font-family: ${fonts.default};
    font-weight: bold;
    cursor: pointer;
    &:hover {
      color: ${({ color }) => color};
    }
    @media ${device.sm} {
      font-size: 11px;
    }
  `;

  const TdActions = styled.td`
    text-align: end;
    width: 0% !important;
  `;

  const Tbody = styled.tbody`
    font-family: ${fonts.medium};
    font-size: ${fontSizes.sm}px;
    .link-name-document {
      cursor: pointer;
      &:hover {
        color: ${colors.suvaGrey};
      }
    }
  `;

  const getNameClient = (clientId) => {
    const findClientName = clients.find((x) => x.clientId === clientId);

    return findClientName?.clientName;
  };

  const onRemove = (currentDocument) => {
    setSelectedRemoveDocument(currentDocument);
    setShowConfirmRemoveDocument(true);
  };

  const onOkSuccessRemove = () => {
    setSelectedRemoveDocument(null);
    onToggleSuccessRemove();
    refreshDocumentsData();
  };

  const onCancelRemove = () => {
    setShowConfirmRemoveDocument(false);
    setSelectedRemoveDocument(null);
  };

  const removeSuccess = () => {
    try {
      if (SelectedRemoveDocument !== undefined && SelectedRemoveDocument !== null) {
        setShowConfirmRemoveDocument(false);
        onToggleSuccessRemove();
      }
    } catch (ex) {
      throw new Error(ex);
    }
  };

  const onOkRemove = async () => {
    try {
      const { clientNavisionId, clientId, id } = SelectedRemoveDocument;
      if (loadingDelete) {
        return;
      }
      const {
        data: { errors },
      } = await deleteDocumentation({
        url: `${window.API_V1}/cli/items/${clientNavisionId}/${clientId}/${encodeURIComponent(
          mail
        )}/${id}`,
      });
      if (errors.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      removeSuccess();
    } catch (ex) {
      setShowConfirmRemoveDocument(false);
      setMessageError(t('custom_field_delete_error_message'));
      onToggleErrorRemove();
    }
  };

  const onDownload = async (document, index) => {
    try {
      setSelecteIndex(index);
      const { clientNavisionId, clientId, id, name } = document;
      const { data, errors } = await getDownload({
        url: `${window.API_V1}/cli/items/${clientNavisionId}/${clientId}/${encodeURIComponent(
          mail
        )}/download/${id}`,
      });
      if (data) {
        const url = `${window.REACT_APP_SERVER_URL}${window.API_V1}/cli/items/${clientNavisionId}/${
          document.clientId
        }/${encodeURIComponent(mail)}/download/${id}`;
        const result = await fetch(url, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const blob = await result.blob();
        const tempLink = window.document.createElement('a');
        tempLink.href = window.URL.createObjectURL(blob);
        tempLink.setAttribute('download', name);
        tempLink.click();
      }
      if (errors?.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      setMessageError(t('custom_field_operation_error_message'));
      onToggleErrorRemove();
    }
  };

  return (
    <>
      <NavHome tabs className="home__document-table" color={mainColor}>
        <NavItem>
          <NavLink
            className="text-uppercase ml-1"
            border={mainColor}
            color={activeTab === '1' ? colors.white : mainColor}
            background={activeTab === '1' ? mainColor : colors.white}
            onClick={() => {
              toggleDocument('1');
            }}
          >
            {t('home_client_section_tab_last_movement')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className="text-uppercase"
            border={mainColor}
            color={activeTab === '2' ? colors.white : mainColor}
            background={activeTab === '2' ? mainColor : colors.white}
            onClick={() => {
              toggleDocument('2');
            }}
          >
            {t('home_document_section_tab_last_document_client')}
          </NavLink>
        </NavItem>
      </NavHome>
      <TabContent activeTab={activeTab}>
        <TabPane tabId="1">
          <Row>
            <Col sm="12">
              <Table
                className={
                  isCustomer
                    ? 'home__document-table home-mobile-table'
                    : 'home__document-table mt-4'
                }
              >
                <thead className="home-thead">
                  {isCustomer ? (
                    <tr>
                      <th>{t('general_client')}</th>
                      <th>{t('general_document')}</th>
                      <th>{}</th>
                    </tr>
                  ) : (
                    <tr />
                  )}
                </thead>
                <Tbody>
                  {lastUpdatedDocuments.map((document, index) => (
                    <tr key={document.id}>
                      {isCustomer && (
                        <TdNameClient id={`client-id-${index}`}>
                          <div>
                            <p>{getNameClient(document.clientId)}</p>
                          </div>
                          <UncontrolledTooltip
                            placement="bottom"
                            target={`client-id-${index}`}
                            autohide={false}
                          >
                            {getNameClient(document.clientId)}
                          </UncontrolledTooltip>
                        </TdNameClient>
                      )}
                      <TdNameDocument id={`name-${index}`}>
                        {isCustomer ? (
                          <div
                            aria-hidden="true"
                            onClick={() => onEdit(document)}
                            className="link-name-document"
                          >
                            <p>{document.name}</p>
                          </div>
                        ) : (
                          <div aria-hidden="true">
                            <p>{document.name}</p>
                          </div>
                        )}

                        <UncontrolledTooltip
                          placement="bottom"
                          target={`name-${index}`}
                          autohide={false}
                        >
                          {document.name ?? ''}
                        </UncontrolledTooltip>
                      </TdNameDocument>
                      <TdActions>
                        <div className="d-flex">
                          <>
                            {!loadingDownload ? (
                              <ButtonIcon
                                id={`download-icon-document-${index}`}
                                title={t('general_button_download')}
                                type="button"
                                Icon={() => <DownloadIcon fill={colors.suvaGrey} />}
                                onClick={() => onDownload(document, index)}
                                className="button-icon"
                              />
                            ) : (
                              <>{selecteIndex === index && <Spinner color={mainColor} />}</>
                            )}
                          </>
                          {isCustomer && (
                            <>
                              <ButtonIcon
                                id={`trash-icon-document-${index}`}
                                title={t('general_delete')}
                                type="button"
                                onClick={() => onRemove(document)}
                                Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                                className="button-icon ml-2"
                              />
                              <ButtonIcon
                                id={`edit-icon-document-${index}`}
                                title={t('clients_edit_client')}
                                type="button"
                                Icon={() => <EditIcon fill={colors.suvaGrey} />}
                                onClick={() => onEdit(document)}
                                className="button-icon"
                              />
                            </>
                          )}
                        </div>
                      </TdActions>
                    </tr>
                  ))}
                </Tbody>
              </Table>
              {!lastUpdatedDocuments.length && (
                <div
                  className="d-flex flex-column justify-content-center align-items-center"
                  onClick={() => history.push('/documentations')}
                  aria-hidden="true"
                  role="button"
                >
                  <DocumentIcon width={57} fill={colors.suvaGrey} height={65} className="mb-2" />
                  <span>{t('document_not_found')}</span>
                </div>
              )}
            </Col>
          </Row>
        </TabPane>
        <TabPane tabId="2">
          <Row>
            <Col sm="12">
              <Table
                className={
                  isCustomer
                    ? 'home__document-table home-mobile-table'
                    : 'home__document-table mt-4'
                }
              >
                <thead className="home-thead">
                  {isCustomer ? (
                    <tr>
                      <th>{t('general_client')}</th>
                      <th>{t('general_document')}</th>
                      <th>{}</th>
                    </tr>
                  ) : (
                    <tr />
                  )}
                </thead>
                <Tbody>
                  {lastCreatedDocuments.map((document, index) => (
                    <tr key={document.id}>
                      {isCustomer && (
                        <TdNameClient id={`client-id-${index}`}>
                          <div>
                            <p>{getNameClient(document.clientId)}</p>
                          </div>
                          <UncontrolledTooltip
                            placement="bottom"
                            target={`client-id-${index}`}
                            autohide={false}
                          >
                            {getNameClient(document.clientId)}
                          </UncontrolledTooltip>
                        </TdNameClient>
                      )}
                      <TdNameDocument>
                        {isCustomer ? (
                          <div
                            aria-hidden="true"
                            onClick={() => onEdit(document)}
                            className="link-name-document"
                          >
                            <p>{document.name}</p>
                          </div>
                        ) : (
                          <div aria-hidden="true">
                            <p>{document.name}</p>
                          </div>
                        )}
                      </TdNameDocument>
                      <TdActions>
                        <div className="d-flex">
                          <>
                            {!loadingDownload ? (
                              <ButtonIcon
                                id={`download-icon-document-${index}`}
                                title={t('general_button_download')}
                                type="button"
                                Icon={() => <DownloadIcon fill={colors.suvaGrey} />}
                                onClick={() => onDownload(document, index)}
                                className="button-icon"
                              />
                            ) : (
                              <>{selecteIndex === index && <Spinner color={mainColor} />}</>
                            )}
                          </>
                          {isCustomer && (
                            <>
                              <ButtonIcon
                                id={`trash-icon-document-${index}`}
                                title={t('general_delete')}
                                type="button"
                                onClick={() => onRemove(document)}
                                Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                                className="button-icon ml-2"
                              />
                              <ButtonIcon
                                id={`edit-icon-document-${index}`}
                                title={t('clients_edit_client')}
                                type="button"
                                Icon={() => <EditIcon fill={colors.suvaGrey} />}
                                onClick={() => onEdit(document)}
                                className="button-icon"
                              />
                            </>
                          )}
                        </div>
                      </TdActions>
                    </tr>
                  ))}
                </Tbody>
              </Table>
              {!lastCreatedDocuments.length && (
                <div
                  className="d-flex flex-column justify-content-center align-items-center"
                  onClick={() => history.push('/documentations')}
                  aria-hidden="true"
                  role="button"
                >
                  <DocumentIcon width={57} fill={colors.suvaGrey} height={65} className="mb-2" />
                  <span>{t('document_not_found')}</span>
                </div>
              )}
            </Col>
          </Row>
        </TabPane>
      </TabContent>
      <ConfirmModal
        isOpen={showConfirmRemoveDocument}
        toggle={onCancelRemove}
        confirmMessage={t('custom_feild_delete_document_confirmation')}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelRemove}
        onOk={onOkRemove}
      />
      <AlertModal
        type="error"
        isOpen={showErrorRemove}
        toggle={onToggleErrorRemove}
        message={messageError}
        okText={t('general_button_ok')}
        onOk={onToggleErrorRemove}
      />
      <AlertModal
        type="success"
        isOpen={showSuccessRemove}
        toggle={onOkSuccessRemove}
        message={t('custom_feild_delete_field_success').replace(
          '{fieldName}',
          SelectedRemoveDocument?.name
        )}
        okText={t('general_button_ok')}
        onOk={onOkSuccessRemove}
      />
    </>
  );
};

HomeTab.propTypes = {
  toggleDocument: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
  lastCreatedDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
  lastUpdatedDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
  refreshDocumentsData: PropTypes.func.isRequired,
};

export default HomeTab;
